import { Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import { Fragment, useEffect } from 'react'
import { ContentView, useGetContentByIdLazyQuery } from '../../../../api/client'
import ContentClassificationList from '../../content-card/ContentClassificationList'

type ChatAssistantContentDrawerProps = {
    open: boolean
    onClose: () => void
    contentId: number | null
}

export default function ChatAssistantContentDrawer({
    open,
    onClose,
    contentId
}: ChatAssistantContentDrawerProps) {
    const [fetchContent, { data, loading, error }] = useGetContentByIdLazyQuery({
        fetchPolicy: 'cache-first'
    })

    useEffect(() => {
        if (open && contentId) {
            fetchContent({ variables: { id: contentId } })
        }
    }, [open, contentId, fetchContent])

    const content = data?.getContentById

    return (
        <Transition show={open} as={Fragment}>
            <div className="fixed inset-0 z-50 overflow-hidden">
                {/* Backdrop */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div
                        className="fixed inset-0 bg-slate-900/30"
                        onClick={onClose}
                        aria-hidden="true"
                    />
                </Transition.Child>

                {/* Drawer panel */}
                <div className="fixed inset-y-0 right-0 flex max-w-full">
                    <Transition.Child
                        as={Fragment}
                        enter="transform transition ease-out duration-300"
                        enterFrom="translate-x-full"
                        enterTo="translate-x-0"
                        leave="transform transition ease-in duration-200"
                        leaveFrom="translate-x-0"
                        leaveTo="translate-x-full"
                    >
                        <div className="w-screen max-w-lg">
                            <div className="flex h-full flex-col bg-white shadow-xl">
                                {/* Header */}
                                <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                                    <h2 className="text-lg font-semibold text-slate-900">
                                        Referenced Content
                                    </h2>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="flex-1 overflow-y-auto px-6 py-6">
                                    {loading && (
                                        <div className="flex items-center justify-center py-12">
                                            <span className="inline-flex h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
                                            <span className="ml-3 text-slate-500">
                                                Loading content...
                                            </span>
                                        </div>
                                    )}

                                    {error && (
                                        <div className="rounded-lg bg-red-50 p-4 text-red-700">
                                            Failed to load content. Please try again.
                                        </div>
                                    )}

                                    {!loading && !error && content && (
                                        <ContentPreview content={content} />
                                    )}

                                    {!loading && !error && !content && contentId && (
                                        <div className="rounded-lg bg-slate-50 p-4 text-slate-500">
                                            Content not found.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </div>
        </Transition>
    )
}

type ContentPreviewProps = {
    content: ContentView
}

function ContentPreview({ content }: ContentPreviewProps) {
    const formattedDate = content.datePosted
        ? dayjs(content.datePosted).format('MMM D, YYYY [at] h:mm A')
        : 'Unknown date'

    const platformIcon = getPlatformIcon(content.socialMediaServiceName)

    return (
        <div className="space-y-6">
            {/* Platform and metadata */}
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                        {platformIcon}
                    </div>
                    <div>
                        <p className="font-medium text-slate-900">
                            {content.serviceAuthorName || 'Unknown Author'}
                        </p>
                        <p className="text-sm text-slate-500">
                            {content.socialMediaServiceName}
                        </p>
                    </div>
                </div>
                <span className="text-xs text-slate-400">ID: {content.id}</span>
            </div>

            {/* Date */}
            <div className="text-sm text-slate-500">{formattedDate}</div>

            {/* Content text */}
            <div className="rounded-lg bg-slate-50 p-4">
                <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                    {content.content || 'No content text available.'}
                </p>
            </div>

            {/* Media preview */}
            {content.media && content.media.length > 0 && (
                <div className="space-y-2">
                    <h4 className="text-sm font-medium text-slate-700">Media</h4>
                    <div className="grid grid-cols-2 gap-2">
                        {content.media.map((mediaItem, index) => (
                            <div
                                key={index}
                                className="aspect-video rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-sm"
                            >
                                {mediaItem.mediaType || 'Media'}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Classifications */}
            <div className="space-y-2">
                <h4 className="text-sm font-medium text-slate-700">Classifications</h4>
                <ContentClassificationList content={content} />
            </div>

            {/* Moderation status */}
            {content.moderation && (
                <div className="space-y-2">
                    <h4 className="text-sm font-medium text-slate-700">Moderation Status</h4>
                    <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${getModerationBadgeClasses(content.moderation)}`}
                    >
                        {content.moderation}
                    </span>
                </div>
            )}

            {/* Link to original */}
            {content.socialMediaContentLink && (
                <div className="border-t border-slate-200 pt-4">
                    <a
                        href={content.socialMediaContentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                        View on {content.socialMediaServiceName}
                        <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                        </svg>
                    </a>
                </div>
            )}
        </div>
    )
}

function getPlatformIcon(platform: string): JSX.Element {
    const iconClasses = 'h-5 w-5'
    const platformLower = platform.toLowerCase()

    if (platformLower.includes('twitter') || platformLower.includes('x')) {
        return (
            <svg className={iconClasses} fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
        )
    }

    if (platformLower.includes('facebook')) {
        return (
            <svg className={iconClasses} fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
        )
    }

    if (platformLower.includes('instagram')) {
        return (
            <svg className={iconClasses} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
            </svg>
        )
    }

    if (platformLower.includes('youtube')) {
        return (
            <svg className={iconClasses} fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
        )
    }

    if (platformLower.includes('tiktok')) {
        return (
            <svg className={iconClasses} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
            </svg>
        )
    }

    // Default icon
    return (
        <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
        </svg>
    )
}

function getModerationBadgeClasses(moderation: string): string {
    const moderationLower = moderation.toLowerCase()

    if (moderationLower === 'severe' || moderationLower === 'high') {
        return 'bg-red-100 text-red-700'
    }

    if (moderationLower === 'moderate' || moderationLower === 'medium') {
        return 'bg-yellow-100 text-yellow-700'
    }

    if (moderationLower === 'low' || moderationLower === 'safe') {
        return 'bg-green-100 text-green-700'
    }

    return 'bg-slate-100 text-slate-700'
}
